// src/pages/components/MessageUsTab.jsx

import React, {
    useEffect,
    useMemo,
    useState,
} from "react";
import axios from "axios";

/* =========================================================
   API
========================================================= */

const MESSAGE_API =
    "https://oppiesfunc.azurewebsites.net/api/submitContactMessage?code=XWx9CcfjBFrhQ1McpNtsYCPgBZ-0W5SHTHSyH4b-AIRdAzFu1-iGhw==";

const MAX_MESSAGE_LENGTH = 2000;

/* =========================================================
   COMPONENT
========================================================= */

function MessageUsTab({
    shop,
    userEmail,
    isManager = false,
}) {
    const [message, setMessage] =
        useState("");

    const [status, setStatus] =
        useState(null);

    const [sending, setSending] =
        useState(false);

    /*
     * Managers default to their Head Office /
     * authenticated account email.
     */
    const [replySource, setReplySource] =
        useState("manager");

    /* =======================================================
       SHOP / ACCOUNT IDENTITY
    ======================================================= */

    const shopName =
        shop?.name?.trim() ||
        "Your Op Shop";

    const shopEmail =
        shop?.email
            ?.trim()
            .toLowerCase() ||
        "";

    const shopId =
        shop?.shopId?.trim() ||
        "";

    /*
     * IMPORTANT:
     *
     * This is the authenticated Portal account.
     *
     * For a normal shop:
     *   authenticatedEmail === shopEmail
     *
     * For a Head Office manager:
     *   authenticatedEmail = Head Office email
     *   shopEmail          = selected branch email
     */
    const authenticatedEmail =
        (
            userEmail ||
            localStorage.getItem(
                "shopEmail"
            ) ||
            ""
        )
            .trim()
            .toLowerCase();

    /*
     * Only show the selector when this genuinely
     * gives the manager two different choices.
     */
    const managerCanChooseReply =
        isManager &&
        authenticatedEmail &&
        shopEmail &&
        authenticatedEmail !== shopEmail;

    /*
     * Address OPPIES should reply to.
     */
    const selectedReplyEmail =
        managerCanChooseReply &&
            replySource === "shop"
            ? shopEmail
            : authenticatedEmail ||
            shopEmail;

    /* =======================================================
       RESET WHEN MANAGER CHANGES BRANCH
    ======================================================= */

    useEffect(() => {
        /*
         * Prevent:
         *
         * Start message as Kerikeri
         * ↓
         * switch Dashboard to Whangārei
         * ↓
         * accidentally send Kerikeri's draft
         *    as Whangārei.
         */

        setReplySource("manager");

        setMessage("");

        setStatus(null);

    }, [
        shopId,
        shopEmail,
        authenticatedEmail,
    ]);

    /* =======================================================
       MESSAGE STATE
    ======================================================= */

    const charactersRemaining =
        MAX_MESSAGE_LENGTH -
        message.length;

    const canSend =
        message.trim().length > 0 &&
        !sending;

    const initial =
        useMemo(() => {
            return (
                shopName
                    .slice(0, 1)
                    .toUpperCase() ||
                "O"
            );
        }, [shopName]);

    /* =======================================================
       SEND MESSAGE
    ======================================================= */

    const handleSend =
        async () => {
            const cleanMessage =
                message.trim();

            if (!cleanMessage) {
                setStatus({
                    type: "error",

                    message:
                        "Please write a message before sending.",
                });

                return;
            }

            if (!shopId) {
                setStatus({
                    type: "error",

                    message:
                        "We couldn't identify the selected shop. Please refresh the portal and try again.",
                });

                return;
            }

            if (!authenticatedEmail) {
                setStatus({
                    type: "error",

                    message:
                        "We couldn't identify your signed-in account. Please sign out and back in, then try again.",
                });

                return;
            }

            if (!shopEmail) {
                setStatus({
                    type: "error",

                    message:
                        "We couldn't identify the selected shop email. Please refresh the portal and try again.",
                });

                return;
            }

            if (
                cleanMessage.length >
                MAX_MESSAGE_LENGTH
            ) {
                setStatus({
                    type: "error",

                    message:
                        `Please keep your message under ${MAX_MESSAGE_LENGTH} characters.`,
                });

                return;
            }

            try {
                setSending(true);

                setStatus(null);

                await axios.post(
                    MESSAGE_API,
                    {
                        /*
                         * Selected branch.
                         */

                        shopId,

                        shopName,

                        shopEmail,

                        /*
                         * Authenticated Portal account.
                         */

                        userEmail:
                            authenticatedEmail,

                        /*
                         * Address OPPIES should
                         * reply to.
                         */

                        senderEmail:
                            selectedReplyEmail,

                        senderRole:
                            "opshop",

                        firstName:
                            shopName,

                        subject:
                            `Message from Web Portal — ${shopName}`,

                        contactPreference:
                            managerCanChooseReply
                                ? replySource
                                : authenticatedEmail ===
                                    shopEmail
                                    ? "shop"
                                    : "manager",

                        message:
                            cleanMessage,
                    }
                );

                setMessage("");

                setStatus({
                    type:
                        "success",

                    message:
                        `Thanks — your message has been sent to the OPPIES team. Replies will be directed to ${selectedReplyEmail}.`,
                });

            } catch (err) {
                console.error(
                    "Failed to send message:",
                    err?.response?.data ||
                    err?.message ||
                    err
                );

                const backendMessage =
                    err?.response
                        ?.data
                        ?.error;

                setStatus({
                    type:
                        "error",

                    message:
                        backendMessage ||
                        "We couldn't send your message. Please try again.",
                });

            } finally {
                setSending(false);
            }
        };

    /* =======================================================
       TEXTAREA KEYBOARD SHORTCUT
    ======================================================= */

    const handleKeyDown =
        (event) => {
            if (
                (
                    event.ctrlKey ||
                    event.metaKey
                ) &&
                event.key ===
                "Enter"
            ) {
                event.preventDefault();

                if (canSend) {
                    handleSend();
                }
            }
        };

    return (
        <>
            <style>
                {messageStyles}
            </style>

            <div className="message-page">

                {/* ================================================
                    INTRO
                ================================================ */}

                <section className="message-intro">

                    <div className="message-eyebrow">
                        OPPIES Support
                    </div>

                    <h1>
                        Message Us
                    </h1>

                    <p>
                        Need a hand with your
                        listing, portal, sales,
                        account or anything else?
                        Send the OPPIES team a
                        message below.
                    </p>

                </section>

                {/* ================================================
                    MAIN CARD
                ================================================ */}

                <section className="message-shell">

                    {/* ============================================
                        LEFT / INFO
                    ============================================ */}

                    <aside className="message-sidebar">

                        <div className="message-sidebar-content">

                            <div className="message-support-icon">

                                <svg
                                    viewBox="0 0 24 24"
                                    width="24"
                                    height="24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.6"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4Z" />

                                    <path d="M8 9h8" />

                                    <path d="M8 13h5" />
                                </svg>

                            </div>

                            <div className="message-sidebar-eyebrow">
                                We're here to help
                            </div>

                            <h2>
                                Contact the OPPIES
                                team
                            </h2>

                            <p>
                                Your message will be
                                sent directly through
                                the OPPIES portal with
                                your shop details
                                attached.
                            </p>

                            <div className="message-help-list">

                                <div className="message-help-item">

                                    <div className="message-help-check">
                                        ✓
                                    </div>

                                    <span>
                                        Portal and account
                                        help
                                    </span>

                                </div>

                                <div className="message-help-item">

                                    <div className="message-help-check">
                                        ✓
                                    </div>

                                    <span>
                                        Shop listing
                                        updates
                                    </span>

                                </div>

                                <div className="message-help-item">

                                    <div className="message-help-check">
                                        ✓
                                    </div>

                                    <span>
                                        Sales and
                                        notifications
                                    </span>

                                </div>

                                <div className="message-help-item">

                                    <div className="message-help-check">
                                        ✓
                                    </div>

                                    <span>
                                        General OPPIES
                                        support
                                    </span>

                                </div>

                                <div className="message-help-item">

                                    <div className="message-help-check">
                                        ✓
                                    </div>

                                    <span>
                                        OR General
                                        feedback / suggestions
                                    </span>

                                </div>

                            </div>

                        </div>

                    </aside>

                    {/* ============================================
                        FORM
                    ============================================ */}

                    <div className="message-form-side">

                        {/* ========================================
                            SENDING AS
                        ======================================== */}

                        <div className="message-sender">

                            <div className="message-avatar">
                                {initial}
                            </div>

                            <div className="message-sender-details">

                                <span>
                                    Sending as
                                </span>

                                <strong>
                                    {shopName}
                                </strong>

                                {shopEmail ? (
                                    <small>
                                        {shopEmail}
                                    </small>
                                ) : null}

                            </div>

                        </div>

                        {/* ========================================
                            MANAGER REPLY EMAIL
                        ======================================== */}

                        {managerCanChooseReply ? (

                            <div className="message-reply-panel">

                                <div className="message-reply-heading">
                                    Reply / contact email
                                </div>

                                <p>
                                    This message is being
                                    sent for {shopName}.
                                    Choose where you would
                                    like the OPPIES team
                                    to reply.
                                </p>

                                <div className="message-reply-options">

                                    {/* MANAGER EMAIL */}

                                    <label
                                        className={
                                            `message-reply-option ${replySource ===
                                                "manager"
                                                ? "message-reply-option-active"
                                                : ""
                                            }`
                                        }
                                    >

                                        <input
                                            type="radio"
                                            name="messageReplySource"
                                            value="manager"
                                            checked={
                                                replySource ===
                                                "manager"
                                            }
                                            onChange={() => {
                                                setReplySource(
                                                    "manager"
                                                );

                                                setStatus(
                                                    null
                                                );
                                            }}
                                            disabled={
                                                sending
                                            }
                                        />

                                        <span className="message-reply-radio" />

                                        <span className="message-reply-copy">

                                            <strong>
                                                Head Office / Manager
                                            </strong>

                                            <small>
                                                {authenticatedEmail}
                                            </small>

                                        </span>

                                        <span className="message-reply-badge">
                                            Recommended
                                        </span>

                                    </label>

                                    {/* SHOP EMAIL */}

                                    <label
                                        className={
                                            `message-reply-option ${replySource ===
                                                "shop"
                                                ? "message-reply-option-active"
                                                : ""
                                            }`
                                        }
                                    >

                                        <input
                                            type="radio"
                                            name="messageReplySource"
                                            value="shop"
                                            checked={
                                                replySource ===
                                                "shop"
                                            }
                                            onChange={() => {
                                                setReplySource(
                                                    "shop"
                                                );

                                                setStatus(
                                                    null
                                                );
                                            }}
                                            disabled={
                                                sending
                                            }
                                        />

                                        <span className="message-reply-radio" />

                                        <span className="message-reply-copy">

                                            <strong>
                                                Selected Op Shop
                                            </strong>

                                            <small>
                                                {shopEmail}
                                            </small>

                                        </span>

                                    </label>

                                </div>

                            </div>

                        ) : null}

                        {/* ========================================
                            FORM HEADING
                        ======================================== */}

                        <div className="message-form-heading">

                            <div>

                                <div className="message-form-eyebrow">
                                    New message
                                </div>

                                <h2>
                                    How can we help?
                                </h2>

                            </div>

                        </div>

                        {/* ========================================
                            TEXTAREA
                        ======================================== */}

                        <div className="message-field">

                            <label
                                htmlFor="oppies-support-message"
                            >
                                Your message
                            </label>

                            <div
                                className={
                                    `message-textarea-wrap ${message.length >
                                        MAX_MESSAGE_LENGTH
                                        ? "message-textarea-error"
                                        : ""
                                    }`
                                }
                            >

                                <textarea
                                    id="oppies-support-message"
                                    rows={8}
                                    value={
                                        message
                                    }
                                    onChange={
                                        (
                                            event
                                        ) => {
                                            setMessage(
                                                event
                                                    .target
                                                    .value
                                            );

                                            if (
                                                status
                                            ) {
                                                setStatus(
                                                    null
                                                );
                                            }
                                        }
                                    }
                                    onKeyDown={
                                        handleKeyDown
                                    }
                                    placeholder="Tell us what you need help with..."
                                    maxLength={
                                        MAX_MESSAGE_LENGTH
                                    }
                                    disabled={
                                        sending
                                    }
                                />

                                <div className="message-textarea-footer">

                                    <span>
                                        Ctrl / Cmd +
                                        Enter to send
                                    </span>

                                    <span
                                        className={
                                            charactersRemaining <
                                                100
                                                ? "message-count-low"
                                                : ""
                                        }
                                    >
                                        {
                                            message.length
                                        }
                                        {" / "}
                                        {
                                            MAX_MESSAGE_LENGTH
                                        }
                                    </span>

                                </div>

                            </div>

                        </div>

                        {/* ========================================
                            STATUS
                        ======================================== */}

                        {status ? (

                            <div
                                className={
                                    `message-status ${status.type ===
                                        "success"
                                        ? "message-status-success"
                                        : "message-status-error"
                                    }`
                                }
                            >

                                <div className="message-status-icon">

                                    {status.type ===
                                        "success" ? (

                                        <svg
                                            viewBox="0 0 24 24"
                                            width="15"
                                            height="15"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="m5 12 4 4L19 6" />
                                        </svg>

                                    ) : (

                                        <span>
                                            !
                                        </span>

                                    )}

                                </div>

                                <span>
                                    {status.message}
                                </span>

                            </div>

                        ) : null}

                        {/* ========================================
                            ACTION
                        ======================================== */}

                        <div className="message-actions">

                            <div className="message-security-note">

                                <svg
                                    viewBox="0 0 24 24"
                                    width="13"
                                    height="13"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <rect
                                        x="4"
                                        y="10"
                                        width="16"
                                        height="10"
                                        rx="2"
                                    />

                                    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                                </svg>

                                {managerCanChooseReply
                                    ? `Sent securely as ${shopName}. Replies to ${selectedReplyEmail}.`
                                    : "Sent securely through your OPPIES account"
                                }

                            </div>

                            <button
                                type="button"
                                className="message-send-button"
                                onClick={
                                    handleSend
                                }
                                disabled={
                                    !canSend
                                }
                            >

                                {sending ? (
                                    <>
                                        <span className="message-spinner" />

                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        Send Message

                                        <svg
                                            viewBox="0 0 24 24"
                                            width="14"
                                            height="14"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.9"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="m22 2-7 20-4-9-9-4Z" />

                                            <path d="M22 2 11 13" />
                                        </svg>
                                    </>
                                )}

                            </button>

                        </div>

                    </div>

                </section>

            </div>
        </>
    );
}

export default MessageUsTab;

/* =========================================================
   STYLES
========================================================= */

const messageStyles = `

  .message-page {
    color: #26332d;
    font-family:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  /* ======================================================
     INTRO
  ====================================================== */

  .message-intro {
    margin-bottom: 20px;
  }

  .message-eyebrow,
  .message-form-eyebrow,
  .message-sidebar-eyebrow {
    color: #047857;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .message-intro h1 {
    margin: 7px 0;
    color: #17211c;
    font-size:
      clamp(
        27px,
        3vw,
        37px
      );
    line-height: 1.08;
    letter-spacing: -0.035em;
  }

  .message-intro p {
    max-width: 650px;
    margin: 0;
    color: #64748b;
    font-size: 14px;
    line-height: 1.65;
  }

  /* ======================================================
     SHELL
  ====================================================== */

  .message-shell {
    display: grid;
    grid-template-columns:
      minmax(220px, 0.72fr)
      minmax(0, 1.55fr);
    overflow: hidden;
    border: 1px solid #e3dbc9;
    border-radius: 22px;
    background: #ffffff;
    box-shadow:
      0 8px 25px
      rgba(
        52,
        48,
        37,
        0.05
      );
  }

  /* ======================================================
     BEIGE SIDE
  ====================================================== */

  .message-sidebar {
    position: relative;
    padding: 27px 24px;
    border-right: 1px solid #e3dbc9;
    background:
      linear-gradient(
        145deg,
        #fbf6e9 0%,
        #f5edd9 100%
      );
  }

  .message-sidebar::after {
    position: absolute;
    right: -45px;
    bottom: -55px;
    width: 160px;
    height: 160px;
    border: 28px solid
      rgba(
        4,
        120,
        87,
        0.045
      );
    border-radius: 50%;
    content: "";
    pointer-events: none;
  }

  .message-sidebar-content {
    position: relative;
    z-index: 1;
  }

  .message-support-icon {
    display: grid;
    width: 47px;
    height: 47px;
    margin-bottom: 25px;
    place-items: center;
    border: 1px solid #d8cfbb;
    border-radius: 14px;
    background:
      rgba(
        255,
        255,
        255,
        0.65
      );
    color: #047857;
  }

  .message-sidebar h2 {
    max-width: 270px;
    margin: 7px 0 10px;
    color: #493f30;
    font-size: 20px;
    line-height: 1.18;
    letter-spacing: -0.025em;
  }

  .message-sidebar p {
    max-width: 310px;
    margin: 0;
    color: #746957;
    font-size: 10px;
    line-height: 1.65;
  }

  /* ======================================================
     HELP LIST
  ====================================================== */

  .message-help-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 25px;
  }

  .message-help-item {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #625947;
    font-size: 9px;
    font-weight: 650;
  }

  .message-help-check {
    display: grid;
    width: 19px;
    height: 19px;
    flex: 0 0 auto;
    place-items: center;
    border-radius: 50%;
    background: #e3eee7;
    color: #047857;
    font-size: 8px;
    font-weight: 900;
  }

  /* ======================================================
     FORM SIDE
  ====================================================== */

  .message-form-side {
    padding: 25px;
    background:
      linear-gradient(
        180deg,
        #ffffff 0%,
        #fffefb 100%
      );
  }

  /* ======================================================
     SENDER
  ====================================================== */

  .message-sender {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 23px;
    padding: 10px 12px;
    border: 1px solid #e9e2d3;
    border-radius: 13px;
    background: #fcf8ef;
  }

  .message-avatar {
    display: grid;
    width: 38px;
    height: 38px;
    flex: 0 0 auto;
    place-items: center;
    border-radius: 11px;
    background: #047857;
    color: #ffffff;
    font-size: 13px;
    font-weight: 800;
  }

  .message-sender-details {
    min-width: 0;
  }

  .message-sender-details > span {
    display: block;
    margin-bottom: 1px;
    color: #938875;
    font-size: 7px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
  }

  .message-sender-details strong {
    display: block;
    overflow: hidden;
    color: #453d31;
    font-size: 10px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .message-sender-details small {
    display: block;
    overflow: hidden;
    margin-top: 2px;
    color: #938875;
    font-size: 7px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* ======================================================
     MANAGER REPLY EMAIL
  ====================================================== */

  .message-reply-panel {
    margin: -9px 0 22px;
    padding: 13px;
    border: 1px solid #dfe8e2;
    border-radius: 13px;
    background: #f7fbf9;
  }

  .message-reply-heading {
    color: #31443b;
    font-size: 9px;
    font-weight: 800;
  }

  .message-reply-panel > p {
    margin: 4px 0 11px;
    color: #7b877f;
    font-size: 8px;
    line-height: 1.5;
  }

  .message-reply-options {
    display: grid;
    grid-template-columns:
      repeat(
        2,
        minmax(0, 1fr)
      );
    gap: 8px;
  }

  .message-reply-option {
    position: relative;
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
    padding: 10px;
    border: 1px solid #dce4df;
    border-radius: 10px;
    background: #ffffff;
    cursor: pointer;
    transition:
      border-color 0.16s ease,
      background 0.16s ease,
      box-shadow 0.16s ease;
  }

  .message-reply-option:hover {
    border-color: #a8c7b8;
  }

  .message-reply-option-active {
    border-color: #6ca58b;
    background: #f1f8f4;
    box-shadow:
      0 0 0 2px
      rgba(
        4,
        120,
        87,
        0.045
      );
  }

  .message-reply-option input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }

  .message-reply-radio {
    width: 15px;
    height: 15px;
    flex: 0 0 auto;
    border: 1.5px solid #adb9b2;
    border-radius: 50%;
    background: #ffffff;
    box-shadow:
      inset 0 0 0 3px
      #ffffff;
  }

  .message-reply-option-active
  .message-reply-radio {
    border-color: #047857;
    background: #047857;
  }

  .message-reply-copy {
    min-width: 0;
    flex: 1;
  }

  .message-reply-copy strong {
    display: block;
    color: #3e4a44;
    font-size: 8px;
  }

  .message-reply-copy small {
    display: block;
    overflow: hidden;
    margin-top: 2px;
    color: #89948e;
    font-size: 7px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .message-reply-badge {
    flex: 0 0 auto;
    padding: 3px 5px;
    border-radius: 999px;
    background: #e2f1e9;
    color: #047857;
    font-size: 6px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  /* ======================================================
     FORM HEADING
  ====================================================== */

  .message-form-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    margin-bottom: 16px;
  }

  .message-form-heading h2 {
    margin: 4px 0 0;
    color: #26332d;
    font-size: 18px;
    letter-spacing: -0.025em;
  }

  /* ======================================================
     FIELD
  ====================================================== */

  .message-field label {
    display: block;
    margin-bottom: 6px;
    color: #524c42;
    font-size: 9px;
    font-weight: 800;
  }

  .message-textarea-wrap {
    overflow: hidden;
    border: 1px solid #dcd2bd;
    border-radius: 14px;
    background: #f9f3e7;
    transition:
      border-color 0.18s ease,
      box-shadow 0.18s ease,
      background 0.18s ease;
  }

  .message-textarea-wrap:focus-within {
    border-color: #75a994;
    background: #fcf8ef;
    box-shadow:
      0 0 0 3px
      rgba(
        4,
        120,
        87,
        0.08
      );
  }

  .message-textarea-error {
    border-color: #dc8e8e;
  }

  .message-textarea-wrap textarea {
    box-sizing: border-box;
    display: block;
    width: 100%;
    min-height: 170px;
    padding: 14px 15px 8px;
    border: 0;
    outline: none;
    background: transparent;
    color: #3f443f;
    resize: vertical;
    font: inherit;
    font-size: 10px;
    line-height: 1.65;
  }

  .message-textarea-wrap textarea::placeholder {
    color: #a89e8b;
  }

  .message-textarea-wrap textarea:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  .message-textarea-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 7px 12px 9px;
    color: #9b927f;
    font-size: 7px;
  }

  .message-count-low {
    color: #b45309;
    font-weight: 800;
  }

  /* ======================================================
     STATUS
  ====================================================== */

  .message-status {
    display: flex;
    align-items: center;
    gap: 9px;
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 11px;
    font-size: 9px;
    font-weight: 650;
  }

  .message-status-success {
    border: 1px solid #b9e0cf;
    background: #edf9f3;
    color: #067454;
  }

  .message-status-error {
    border: 1px solid #f2c3c3;
    background: #fff2f2;
    color: #b42323;
  }

  .message-status-icon {
    display: grid;
    width: 22px;
    height: 22px;
    flex: 0 0 auto;
    place-items: center;
    border-radius: 50%;
    background:
      rgba(
        255,
        255,
        255,
        0.75
      );
    font-size: 9px;
    font-weight: 900;
  }

  /* ======================================================
     ACTIONS
  ====================================================== */

  .message-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 16px;
  }

  .message-security-note {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #9a917f;
    font-size: 7px;
    line-height: 1.4;
  }

  .message-security-note svg {
    flex: 0 0 auto;
    color: #8c9c94;
  }

  .message-send-button {
    display: inline-flex;
    min-width: 125px;
    min-height: 38px;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 9px 14px;
    border: 0;
    border-radius: 10px;
    background: #047857;
    color: #ffffff;
    cursor: pointer;
    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
    box-shadow:
      0 5px 12px
      rgba(
        4,
        120,
        87,
        0.14
      );
    transition:
      transform 0.16s ease,
      background 0.16s ease,
      box-shadow 0.16s ease;
  }

  .message-send-button:hover:not(:disabled) {
    background: #036b4e;
    transform: translateY(-1px);
    box-shadow:
      0 7px 15px
      rgba(
        4,
        120,
        87,
        0.18
      );
  }

  .message-send-button:disabled {
    cursor: not-allowed;
    opacity: 0.48;
    box-shadow: none;
  }

  .message-spinner {
    width: 11px;
    height: 11px;
    border: 2px solid
      rgba(
        255,
        255,
        255,
        0.4
      );
    border-top-color: #ffffff;
    border-radius: 50%;
    animation:
      message-spin
      0.75s
      linear
      infinite;
  }

  @keyframes message-spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* ======================================================
     RESPONSIVE
  ====================================================== */

  @media (max-width: 760px) {

    .message-shell {
      grid-template-columns: 1fr;
    }

    .message-sidebar {
      border-right: 0;
      border-bottom:
        1px solid #e3dbc9;
    }

    .message-help-list {
      display: grid;
      grid-template-columns:
        repeat(
          2,
          minmax(0, 1fr)
        );
    }

  }

  @media (max-width: 520px) {

    .message-sidebar,
    .message-form-side {
      padding: 20px;
    }

    .message-help-list {
      grid-template-columns: 1fr;
    }

    .message-reply-options {
      grid-template-columns: 1fr;
    }

    .message-actions {
      align-items: stretch;
      flex-direction: column;
    }

    .message-send-button {
      width: 100%;
    }

  }

`;